import { defineNuxtPlugin } from 'nuxt/app'

// Track the last DOM mutation time and active callbacks
let lastMutationTime = 0
let mutationObserver: MutationObserver | null = null
let activeCallbacks = new Set<number>()

function setupMutationObserver() {
  if (mutationObserver) return

  mutationObserver = new MutationObserver(() => {
    lastMutationTime = Date.now()
  })

  // Observe all changes to the DOM
  mutationObserver.observe(document.body, {
    childList: true,
    attributes: true,
    characterData: true,
    subtree: true,
  })
}

function cleanupMutationObserver() {
  if (mutationObserver && activeCallbacks.size === 0) {
    mutationObserver.disconnect()
    mutationObserver = null
  }
}

function polyfillRequestIdleCallback() {
  if (!window.requestIdleCallback) {
    // eslint-disable-next-line no-console
    console.log('polyfilling requestIdleCallback')
    window.requestIdleCallback =
      requestIdleCallbackPolyfill as typeof window.requestIdleCallback
    window.cancelIdleCallback =
      cancelIdleCallbackPolyfill as typeof window.cancelIdleCallback

    setupMutationObserver()
  }
}

function requestIdleCallbackPolyfill(
  cb: IdleRequestCallback,
  options?: IdleRequestOptions,
): number {
  const start = Date.now()
  const DEFAULT_TIMEOUT = 10000 // Default to 10s which is our common use case
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT
  const MIN_IDLE_TIME = 100 // Consider system idle if no mutations for 100ms

  // We check more frequently at the start, then back off
  const checkInterval = Math.min(200, timeout / 20)

  const checkIdleAndRun = () => {
    const now = Date.now()
    const elapsed = now - start
    const timeSinceLastMutation = now - lastMutationTime

    // If we've exceeded timeout or system seems idle, run the callback
    if (elapsed >= timeout || timeSinceLastMutation >= MIN_IDLE_TIME) {
      activeCallbacks.delete(timeoutId)
      cleanupMutationObserver()

      cb({
        didTimeout: elapsed >= timeout,
        timeRemaining: () => {
          const currentElapsed = Date.now() - start
          // For long timeouts (>1s), we give more generous time slices
          const timeSlice = timeout > 1000 ? 200 : 50
          // Give more time at the start, gradually decreasing
          const remainingTime = Math.max(0, timeout - currentElapsed)
          return Math.min(timeSlice, remainingTime)
        },
      })
      return
    }

    // Otherwise, schedule another check
    setTimeout(checkIdleAndRun, checkInterval)
  }

  // Create and track the timeout ID
  const handle = setTimeout(checkIdleAndRun, checkInterval)
  const timeoutId = Number(handle)
  activeCallbacks.add(timeoutId)
  return timeoutId
}

function cancelIdleCallbackPolyfill(id: number): void {
  clearTimeout(id)
  activeCallbacks.delete(id)
  cleanupMutationObserver()
}

export default defineNuxtPlugin(() => {
  if (process.server) {
    return
  }
  polyfillRequestIdleCallback()
})
